/**
 * useFrustrationDetection — detects "rage clicks" (rapid clicks on the
 * same element that don't seem to change anything) and surfaces Maya's
 * help offer.
 *
 * Heuristic:
 *   • Listen to clicks at window-level.
 *   • Track the last 4 clicks: their target element + timestamp.
 *   • If 3 or more clicks land on the SAME target element within the
 *     last 4 seconds → flag as "frustrated".
 *   • Reset the flag once the customer engages with any beacon, or
 *     once a fresh activity outside the rapid burst happens.
 *
 * The hook intentionally avoids state-change detection (too fragile).
 * Customer interactions usually re-render the React tree, so if the
 * SAME DOM element keeps receiving clicks at high frequency, that's a
 * strong proxy for "this control isn't responding to me."
 *
 * Returns: { isFrustrated, target, reset }
 */
import { useEffect, useRef, useState } from "react";

import useTour from "./useTour";

const SESSION_FIRED_KEY = "jns_tour_frustration_fired";

export default function useFrustrationDetection({
    enabled = true,
    minClicks = 3,
    withinMs = 4000,
} = {}) {
    const [isFrustrated, setIsFrustrated] = useState(false);
    const [target, setTarget] = useState(null);
    const recentClicksRef = useRef([]);
    const { openBeaconId } = useTour();

    useEffect(() => {
        if (!enabled || typeof window === "undefined") return undefined;
        // If we've already nudged the customer about frustration in
        // this session, don't fire again (would feel naggy).
        try {
            if (window.sessionStorage.getItem(SESSION_FIRED_KEY) === "1") {
                return undefined;
            }
        } catch { /* ignore */ }

        function handleClick(e) {
            const now = Date.now();
            // Climb up to the nearest interactive element so we treat
            // clicks anywhere on a button as clicks on that button.
            const interactive = e.target.closest(
                "button, a, [role=button], input, select, textarea, [data-tour-target]"
            ) || e.target;
            // Don't count tour-system clicks as user frustration.
            if (interactive.closest?.("[data-testid^='tour-']")) return;
            if (interactive.closest?.(".jns-tour-bubble, .jns-tour-spotlight, .jns-tour-stuck-nudge")) return;

            const clicks = recentClicksRef.current;
            // Drop old clicks outside the window.
            const fresh = clicks.filter((c) => now - c.at <= withinMs);
            fresh.push({ at: now, target: interactive });
            recentClicksRef.current = fresh.slice(-6);

            const sameTargetCount = fresh.filter((c) => c.target === interactive).length;
            if (sameTargetCount >= minClicks) {
                setIsFrustrated(true);
                setTarget(interactive);
                try { window.sessionStorage.setItem(SESSION_FIRED_KEY, "1"); } catch { /* ignore */ }
                recentClicksRef.current = [];
            }
        }
        window.addEventListener("click", handleClick, true);
        return () => window.removeEventListener("click", handleClick, true);
    }, [enabled, minClicks, withinMs]);

    // Customer engaged a beacon → they're un-stuck, clear the flag.
    useEffect(() => {
        if (openBeaconId && isFrustrated) {
            setIsFrustrated(false);
            setTarget(null);
        }
    }, [openBeaconId, isFrustrated]);

    function reset() {
        setIsFrustrated(false);
        setTarget(null);
        recentClicksRef.current = [];
    }

    return { isFrustrated, target, reset };
}
